import { Controller } from '@hotwired/stimulus'
import { gsap } from 'gsap/dist/gsap'
import { SplitText } from 'gsap/dist/SplitText'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

export default class extends Controller {
    static targets = [
        'headline',
        'substituteText',
        'subheading',
        'square',
        'smallSquare',
        'headerImage',
        'image',
        'navbar',
        'digitalText',
        'button',
        'accordionItem',
    ]

    connect() {
        gsap.registerPlugin(SplitText, ScrollTrigger)

        if (this.hasNavbarTarget) {
            // Fade in the navbar
            // TODO: make the delay a variable, so footer one can be different
            this.navbarTarget.classList.remove('visibility-hidden')
            gsap.from(this.navbarTarget, {
                opacity: 0,
                duration: 0.5,
                ease: 'power2.out',
                delay: 0.75,
                scrollTrigger: {
                    trigger: this.navbarTarget,
                    start: 'top bottom',
                    end: 'bottom top',
                },
            })
        }

        if (this.hasHeaderImageTarget) {
            // Fade in the image
            this.headerImageTarget.classList.remove('visibility-hidden')
            gsap.from(this.headerImageTarget, {
                opacity: 0,
                duration: 2.5,
                ease: 'power2.out',
                delay: 1,
                scrollTrigger: {
                    trigger: this.headerImageTarget,
                    start: 'top bottom',
                    end: 'bottom top',
                },
            })
        }

        if (this.hasImageTarget) {
            // Fade in the image
            this.imageTarget.classList.remove('visibility-hidden')
            gsap.from(this.imageTarget, {
                opacity: 0,
                duration: 2.5,
                ease: 'power2.out',
                delay: 0.25,
                scrollTrigger: {
                    trigger: this.imageTarget,
                    start: 'top bottom',
                    end: 'bottom top',
                },
            })
        }

        if (this.hasButtonTarget) {
            // Fade in the button on scroll
            this.buttonTarget.classList.remove('visibility-hidden')
            gsap.from(this.buttonTarget, {
                opacity: 0,
                duration: 0.5,
                ease: 'power2.out',
                delay: 1.25,
                scrollTrigger: {
                    trigger: this.buttonTarget,
                    start: 'top bottom',
                    end: 'bottom top',
                },
            })
        }

        // Create a few timelines to sequence the animations
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: this.element,
                start: 'top bottom',
                end: 'bottom top',
            },
        })

        if (this.hasDigitalTextTarget) {
            tl.add(() => {
                this.digitalTextTarget.classList.remove('visibility-hidden')
                this.digitalTextAnimation(this.digitalTextTarget, true)
            }, '-=0')
        }

        const tl2 = gsap.timeline({
            scrollTrigger: {
                trigger: this.element,
                start: 'top bottom',
                end: 'bottom top',
            },
        })

        if (this.hasSquareTarget) {
            // Strobing effect to the square
            this.squareTarget.classList.remove('visibility-hidden')
            tl.to(this.squareTarget, {
                opacity: 0.1,
                duration: 0.75,
                repeat: 9,
                yoyo: true,
                ease: 'power1.inOut',
            })
        }

        if (this.hasSmallSquareTarget) {
            // Fade in the small square after 2 seconds, then do strobing effect
            this.smallSquareTarget.classList.remove('visibility-hidden')

            const smallSquareTimeline = gsap.timeline({
                scrollTrigger: {
                    trigger: this.smallSquareTarget,
                    start: 'top bottom',
                    end: 'bottom top',
                },
            })

            smallSquareTimeline.to(this.smallSquareTarget, {
                opacity: 0.1,
                duration: 0.75,
                repeat: 5,
                yoyo: true,
                ease: 'power1.inOut',
            })
        }

        if (this.hasHeadlineTarget) {
            // Stacking text animation for headline and subheading
            this.headlineTarget.classList.remove('visibility-hidden')

            const headline = new SplitText(this.headlineTarget, {
                type: 'lines',
                linesClass: 'overflow-hidden',
            })

            // Add animations to the timeline in sequence
            tl2.from(
                headline.lines,
                {
                    y: 100,
                    opacity: 0,
                    stagger: 0.1,
                    duration: 0.4,
                    ease: 'power2.inOut',
                    delay: 0.25,
                },
                this.hasSquareTarget ? '+=0.75' : '+=0',
            )

            if (this.hasSubheadingTarget) {
                this.subheadingTarget.classList.remove('visibility-hidden')
                const subheading = new SplitText(this.subheadingTarget, {
                    type: 'lines',
                    linesClass: 'overflow-hidden',
                })
                tl2.from(
                    subheading.lines,
                    {
                        y: 100,
                        opacity: 0,
                        stagger: 0.1,
                        duration: 0.4,
                        ease: 'power2.inOut',
                    },
                    '+=0',
                ) // Delay start by 0.2 seconds after headline animation completes
            }

            // Digital text animation for text inside headline
            // Using a class lookup here instead of stimulus target because of a quirk in how it loads.
            // Target is sometimes empty due to the animation system running on it prior.
            const headlineDigitalTextTargets =
                this.headlineTarget.querySelectorAll('.animate-digital-text')
            const headlineDigitalTextTarget =
                headlineDigitalTextTargets[headlineDigitalTextTargets.length - 1]
            if (headlineDigitalTextTarget) {
                // Wait for previous animations to complete then animate the digital text
                tl2.add(() => {
                    this.textSwapAnimation(headlineDigitalTextTarget)
                }, '+=1.5') // Start 1.5 seconds after the subheading animation
            }
        }

        if (this.hasAccordionItemTarget) {
            // Initialize all accordion items as hidden
            this.accordionItemTargets.forEach((item) => {
                item.style.opacity = '0'
                item.style.transform = 'translateY(50px)'
            })

            // Fade each accordion item in on scroll
            const accordionParent = this.accordionItemTargets[0].parentElement
            this.accordionItemTargets.forEach((item, index) => {
                gsap.to(item, {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power2.out',
                    delay: index * 0.2, // Stagger the animations
                    repeat: 0,
                    scrollTrigger: {
                        trigger: accordionParent,
                        start: 'top bottom-=100',
                        end: 'bottom top',
                    },
                })
            })
        }

        // Add strobe animation to bg patterns
        const strobeOverlays = document.querySelectorAll('.rtm-pattern')
        if (strobeOverlays.length > 0) {
            strobeOverlays.forEach((overlay) => {
                // Create a timeline for strobe pattern animation
                const strobeTimeline = gsap.timeline({
                    repeat: -1,
                    scrollTrigger: {
                        trigger: overlay,
                        start: 'top bottom',
                        end: 'bottom top',
                    },
                })

                strobeTimeline
                    .call(() => {
                        overlay.classList.remove('strobe')
                    })
                    .to({}, { duration: 2.5 })
                    .call(() => {
                        overlay.classList.add('strobe')
                    })
                    .to({}, { duration: 1 }) // Keep strobe off for 1 second
            })
        }
    }

    digitalTextAnimation(digitalTextTarget) {
        const originalText = digitalTextTarget.textContent
        const chars = originalText.trim().split('')

        // Clear the target and create individual spans for each character
        digitalTextTarget.innerHTML = ' '
        chars.forEach((char) => {
            const span = document.createElement('span')
            span.innerHTML = char === ' ' ? '&nbsp;' : char
            digitalTextTarget.appendChild(span)
        })

        // Get all the character spans
        const charElements = digitalTextTarget.querySelectorAll('span')

        // Randomly stagger the characters in with a digital blur effect
        gsap.to(charElements, {
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            stagger: {
                each: 0.045,
                from: 'random',
            },
            duration: 0.3,
            ease: 'power2.out',
            repeat: 0,
        })
    }

    textSwapAnimation(digitalTextTarget) {
        const originalText = digitalTextTarget.textContent
        const chars = originalText.trim().split('')

        // Clear the target and create individual spans for each character
        digitalTextTarget.innerHTML = ' '
        chars.forEach((char) => {
            const span = document.createElement('span')
            span.innerHTML = char === ' ' ? '&nbsp;' : char
            digitalTextTarget.appendChild(span)
        })

        // Get all the character spans
        const charElements = digitalTextTarget.querySelectorAll('span')

        // Create flicker effect with reordered characters
        gsap.fromTo(
            charElements,
            {
                opacity: 1,
                visibility: 'visible',
            },
            {
                opacity: 0,
                visibility: 'hidden',
                stagger: {
                    each: 0.05,
                    from: 'end', // Start from the end (right to left)
                },
                duration: 0.25,
                repeat: 0,
                yoyo: true,
                ease: 'power1.inOut',
                onComplete: () => {
                    const substituteText = digitalTextTarget.dataset.substituteText || originalText

                    // After first flicker, replace with new text
                    const newText = substituteText
                    digitalTextTarget.textContent = ''

                    // Create new spans for the new text
                    const newChars = newText.split('')
                    newChars.forEach((char) => {
                        const span = document.createElement('span')
                        span.textContent = char
                        span.style.opacity = 0
                        span.style.visibility = 'hidden'
                        digitalTextTarget.appendChild(span)
                    })

                    // Flicker in the new text
                    const newCharElements = digitalTextTarget.querySelectorAll('span')
                    gsap.fromTo(
                        newCharElements,
                        {
                            opacity: 0,
                            visibility: 'hidden',
                        },
                        {
                            opacity: 1,
                            visibility: 'visible',
                            stagger: {
                                each: 0.05,
                                from: 'start',
                            },
                            duration: 0.25,
                            ease: 'power1.inOut',
                        },
                    )
                },
            },
        )
    }
}
